import { useMemo, useRef, useState } from "react";
import { CollisionPriority } from "@dnd-kit/abstract";
import {
  DragOverlay,
  DragDropProvider,
  type DragDropEventHandlers,
} from "@dnd-kit/react";
import { isSortable, useSortable } from "@dnd-kit/react/sortable";
import {
  KeyboardSensor,
  PointerActivationConstraints,
  PointerSensor,
} from "@dnd-kit/dom";
import { Debug } from "@dnd-kit/dom/plugins/debug";
import type { ListDropIndicatorDirection } from "@/components/foundations/lists/ListDropIndicator/ListDropIndicator";
import SortableDropSlot from "@/components/foundations/lists/SortableDropSlot/SortableDropSlot";
import CustomListSortItem from "../CustomListSortItem/CustomListSortItem";
import {
  formatCategoryDescription,
  formatListDescription,
  moveListAfterEntry,
  moveListOutsideCategory,
  moveSortableListsEntry,
  type SortableCategory,
  type SortableList,
  type SortableListsEntry,
} from "./sortableLists";
import "./_sortable-lists-manager.scss";

const categoryEdgePrefix = "category-edge:";
const listDropSlotPrefix = "list-drop-slot:";

function categoryEdgeId(categoryId: string, placement: "before" | "after") {
  return `${categoryEdgePrefix}${categoryId}:${placement}`;
}

function parseCategoryEdgeId(id: string) {
  if (!id.startsWith(categoryEdgePrefix)) return null;
  const value = id.slice(categoryEdgePrefix.length);
  const separator = value.lastIndexOf(":");
  if (separator === -1) return null;
  const categoryId = value.slice(0, separator);
  const placement = value.slice(separator + 1);
  if (!categoryId || (placement !== "before" && placement !== "after"))
    return null;
  return { categoryId, placement } as const;
}

function parseListDropSlotId(id: string) {
  return id.startsWith(listDropSlotPrefix)
    ? id.slice(listDropSlotPrefix.length)
    : null;
}

export type {
  SortableCategory,
  SortableList,
  SortableListsEntry,
} from "./sortableLists";

function categoryChildren(layout: SortableListsEntry[], categoryId: string) {
  return layout.filter(
    (entry): entry is SortableList =>
      entry.type === "list" && entry.categoryId === categoryId
  );
}

function categoryIsActive(children: SortableList[]) {
  return children.length === 0 || children.some(child => child.active);
}

function SortableEntryRow({
  entry,
  index,
  position,
  categoryActive,
  categoryLists,
  categoryLabel,
  collapsed,
  isGroupDropTarget,
  isBlockDragging,
  onToggleActive,
  onToggleCollapsed,
  onDelete,
  onOpen,
}: {
  entry: SortableListsEntry;
  index: number;
  position?: number;
  categoryActive: boolean;
  categoryLists: SortableList[];
  categoryLabel?: string;
  collapsed: boolean;
  isGroupDropTarget: boolean;
  isBlockDragging: boolean;
  onToggleActive?: () => void;
  onToggleCollapsed?: () => void;
  onDelete?: () => void;
  onOpen?: () => void;
}) {
  const disabled = entry.type === "list" && entry.protectedItem;
  const { handleRef, ref, isDragging } = useSortable({
    id: entry.id,
    disabled: disabled ? { draggable: true, droppable: false } : false,
    index,
    group: "lists",
    // Keep keyboard sorting, but let the committed layout control grouping and indentation.
    plugins: defaults => defaults.slice(0, 1),
    type: entry.type,
    accept: ["list", "category"],
    collisionPriority:
      entry.type === "category"
        ? CollisionPriority.High
        : CollisionPriority.Normal,
    transition: {
      duration: 300,
      easing: "cubic-bezier(0.34, 1.56, 0.64, 1)",
    },
    data: {
      entryType: entry.type,
      categoryId: entry.type === "list" ? entry.categoryId : entry.id,
    },
  });

  return (
    <div
      ref={ref}
      className="relative"
      data-testid={`sortable-entry-${entry.id}`}
      data-entry-type={entry.type}
      data-category-id={
        entry.type === "list" ? (entry.categoryId ?? "") : entry.id
      }
    >
      <CustomListSortItem
        className={isGroupDropTarget ? "drop" : undefined}
        label={entry.label}
        sortIndicatorColors={
          entry.type === "list" ? entry.sortIndicatorColors : undefined
        }
        info={
          entry.type === "category"
            ? formatCategoryDescription(categoryLists)
            : formatListDescription(entry, categoryLabel)
        }
        position={position}
        variant={
          entry.type === "category"
            ? "category"
            : entry.categoryId
              ? "child"
              : "list"
        }
        active={entry.type === "category" ? categoryActive : entry.active}
        expanded={entry.type === "category" ? !collapsed : undefined}
        protectedItem={disabled}
        isDragging={isDragging || isBlockDragging}
        dragHandleRef={disabled ? undefined : handleRef}
        onToggleActive={onToggleActive}
        onDelete={onDelete}
        onOpen={entry.type === "category" ? onToggleCollapsed : onOpen}
      />
    </div>
  );
}

function OverlayEntry({
  entry,
  position,
  layout,
  categoryLabels,
}: {
  entry: SortableListsEntry;
  position?: number;
  layout: SortableListsEntry[];
  categoryLabels: Map<string, string>;
}) {
  const children =
    entry.type === "category" ? categoryChildren(layout, entry.id) : [];
  const noop = () => undefined;

  return (
    <div className="pointer-events-none w-full space-y-2" aria-hidden="true">
      <CustomListSortItem
        label={entry.label}
        sortIndicatorColors={
          entry.type === "list" ? entry.sortIndicatorColors : undefined
        }
        info={
          entry.type === "category"
            ? formatCategoryDescription(children)
            : formatListDescription(
                entry,
                entry.categoryId
                  ? categoryLabels.get(entry.categoryId)
                  : undefined
              )
        }
        position={position}
        variant={
          entry.type === "category"
            ? "category"
            : entry.categoryId
              ? "child"
              : "list"
        }
        active={
          entry.type === "category" ? categoryIsActive(children) : entry.active
        }
        isOverlay
        onToggleActive={noop}
        onDelete={noop}
        onOpen={noop}
      />
      {children.map(child => (
        <CustomListSortItem
          key={child.id}
          label={child.label}
          sortIndicatorColors={child.sortIndicatorColors}
          info={formatListDescription(child, entry.label)}
          position={layout
            .filter(item => item.type === "list")
            .findIndex(item => item.id === child.id)}
          variant="child"
          active={child.active}
          isOverlay
          onToggleActive={noop}
          onDelete={noop}
          onOpen={noop}
        />
      ))}
    </div>
  );
}

export type SortableListsManagerProps = {
  initialLayout: SortableListsEntry[];
  layout?: SortableListsEntry[];
  onLayoutChange?: (layout: SortableListsEntry[]) => void;
  onDeleteList?: (list: SortableList) => void;
  onDeleteCategory?: (category: SortableCategory) => void;
  onOpenList?: (list: SortableList) => void;
  debug?: boolean;
};

export default function SortableListsManager({
  initialLayout,
  layout: controlledLayout,
  onLayoutChange,
  onDeleteList,
  onDeleteCategory,
  onOpenList,
  debug = false,
}: SortableListsManagerProps) {
  const [internalLayout, setInternalLayout] = useState(initialLayout);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const lastTargetId = useRef<string | null>(null);
  const layout = controlledLayout ?? internalLayout;
  const collapsedCategoryIds = useMemo(
    () =>
      new Set(
        layout
          .filter(entry => entry.type === "category" && entry.collapsed)
          .map(entry => entry.id)
      ),
    [layout]
  );
  const visibleLayout = useMemo(
    () =>
      layout.filter(
        entry =>
          entry.type === "category" ||
          !entry.categoryId ||
          !collapsedCategoryIds.has(entry.categoryId)
      ),
    [collapsedCategoryIds, layout]
  );
  const positions = useMemo(() => {
    let index = 0;
    return new Map(
      layout
        .filter((entry): entry is SortableList => entry.type === "list")
        .map(entry => [entry.id, index++])
    );
  }, [layout]);
  const categoryLabels = useMemo(
    () =>
      new Map(
        layout
          .filter(entry => entry.type === "category")
          .map(entry => [entry.id, entry.label])
      ),
    [layout]
  );
  const activeEntry = activeId
    ? (layout.find(entry => entry.id === activeId) ?? null)
    : null;
  const overEntry = overId
    ? (layout.find(entry => entry.id === overId) ?? null)
    : null;
  const dropPresentation = useMemo(() => {
    if (!activeEntry || !overEntry || activeEntry.id === overEntry.id) {
      return {
        groupCategoryId: null as string | null,
        indicatorEntryId: null as string | null,
        indicator: undefined as "before" | "after" | undefined,
      };
    }

    const activeIndex = layout.findIndex(entry => entry.id === activeEntry.id);
    const overIndex = layout.findIndex(entry => entry.id === overEntry.id);
    const movingDown = activeIndex < overIndex;

    if (activeEntry.type === "list") {
      if (overEntry.type === "category") {
        return {
          groupCategoryId: overEntry.id,
          indicatorEntryId: null,
          indicator: undefined,
        };
      }

      return {
        groupCategoryId: null,
        indicatorEntryId: overEntry.id,
        indicator:
          overEntry.type === "list" && overEntry.protectedItem
            ? ("after" as const)
            : movingDown
              ? ("after" as const)
              : ("before" as const),
      };
    }

    const targetCategoryId =
      overEntry.type === "category" ? overEntry.id : overEntry.categoryId;
    if (targetCategoryId) {
      if (targetCategoryId === activeEntry.id) {
        return {
          groupCategoryId: null,
          indicatorEntryId: null,
          indicator: undefined,
        };
      }
      const children = categoryChildren(layout, targetCategoryId);
      return {
        groupCategoryId: null,
        indicatorEntryId: movingDown
          ? (children.at(-1)?.id ?? targetCategoryId)
          : targetCategoryId,
        indicator: movingDown ? ("after" as const) : ("before" as const),
      };
    }

    return {
      groupCategoryId: null,
      indicatorEntryId: overEntry.id,
      indicator:
        overEntry.type === "list" && overEntry.protectedItem
          ? ("after" as const)
          : movingDown
            ? ("after" as const)
            : ("before" as const),
    };
  }, [activeEntry, layout, overEntry]);
  const activeSlot = useMemo(() => {
    if (!activeEntry) return null;
    if (dropPresentation.groupCategoryId) {
      return {
        afterEntryId: dropPresentation.groupCategoryId,
        direction: "in" as const,
      };
    }
    if (!dropPresentation.indicatorEntryId || !dropPresentation.indicator) {
      return null;
    }
    const targetIndex = visibleLayout.findIndex(
      entry => entry.id === dropPresentation.indicatorEntryId
    );
    if (targetIndex === -1) return null;
    const owner =
      dropPresentation.indicator === "after"
        ? visibleLayout[targetIndex]
        : visibleLayout[targetIndex - 1];
    if (!owner) return null;
    return {
      afterEntryId: owner.id,
      direction: (dropPresentation.indicator === "after"
        ? "down"
        : "up") as ListDropIndicatorDirection,
    };
  }, [activeEntry, dropPresentation, visibleLayout]);

  function updateLayout(next: SortableListsEntry[]) {
    if (controlledLayout === undefined) setInternalLayout(next);
    onLayoutChange?.(next);
  }

  function clearDragState() {
    setActiveId(null);
    setOverId(null);
    lastTargetId.current = null;
  }

  const handleDragStart: DragDropEventHandlers["onDragStart"] = event => {
    lastTargetId.current = null;
    setActiveId(String(event.operation.source?.id));
  };

  const handleDragOver: DragDropEventHandlers["onDragOver"] = event => {
    const sourceId = event.operation.source
      ? String(event.operation.source.id)
      : null;
    const targetId = event.operation.target
      ? String(event.operation.target.id)
      : null;
    if (targetId && targetId !== sourceId) lastTargetId.current = targetId;
    setOverId(targetId === sourceId ? lastTargetId.current : targetId);
  };

  const handleDragEnd: DragDropEventHandlers["onDragEnd"] = event => {
    const sourceId = event.operation.source
      ? String(event.operation.source.id)
      : null;
    const operationTargetId = event.operation.target
      ? String(event.operation.target.id)
      : null;
    const optimisticTargetId =
      event.operation.source && isSortable(event.operation.source)
        ? layout[event.operation.source.index]?.id
        : null;
    const targetId =
      operationTargetId === sourceId
        ? (lastTargetId.current ?? optimisticTargetId)
        : operationTargetId;
    clearDragState();
    if (event.canceled || !sourceId || !targetId) return;
    const categoryEdge = parseCategoryEdgeId(targetId);
    if (categoryEdge) {
      updateLayout(
        moveListOutsideCategory(
          layout,
          sourceId,
          categoryEdge.categoryId,
          categoryEdge.placement
        )
      );
      return;
    }
    const afterEntryId = parseListDropSlotId(targetId);
    if (afterEntryId) {
      updateLayout(moveListAfterEntry(layout, sourceId, afterEntryId));
      return;
    }
    updateLayout(moveSortableListsEntry(layout, sourceId, targetId));
  };

  function toggle(entry: SortableListsEntry) {
    if (entry.type === "list") {
      updateLayout(
        layout.map(item =>
          item.id === entry.id ? { ...item, active: !entry.active } : item
        )
      );
      return;
    }
    const children = categoryChildren(layout, entry.id);
    if (children.length === 0) return;
    const nextActive = !categoryIsActive(children);
    updateLayout(
      layout.map(item =>
        item.type === "list" && item.categoryId === entry.id
          ? { ...item, active: nextActive }
          : item
      )
    );
  }

  function toggleCollapsed(categoryId: string) {
    updateLayout(
      layout.map(entry =>
        entry.type === "category" && entry.id === categoryId
          ? { ...entry, collapsed: !entry.collapsed }
          : entry
      )
    );
  }

  return (
    <DragDropProvider
      plugins={debug ? defaults => [...defaults, Debug] : undefined}
      sensors={[
        PointerSensor.configure({
          activationConstraints: () => [
            new PointerActivationConstraints.Distance({ value: 6 }),
          ],
        }),
        KeyboardSensor,
      ]}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="sortable-lists-manager">
        {visibleLayout.map((entry, index) => {
          const nextEntry = visibleLayout[index + 1];
          const children =
            entry.type === "category" ? categoryChildren(layout, entry.id) : [];
          const isActiveCategoryBlock =
            activeEntry?.type === "category" &&
            (entry.id === activeEntry.id ||
              (entry.type === "list" && entry.categoryId === activeEntry.id));
          const isCategoryBlockEnd =
            (entry.type === "category" &&
              !(
                nextEntry?.type === "list" && nextEntry.categoryId === entry.id
              )) ||
            (entry.type === "list" &&
              Boolean(entry.categoryId) &&
              !(
                nextEntry?.type === "list" &&
                nextEntry.categoryId === entry.categoryId
              ));
          const edgeCategoryId = isCategoryBlockEnd
            ? entry.type === "category"
              ? entry.id
              : entry.categoryId
            : nextEntry?.type === "category"
              ? nextEntry.id
              : null;
          const edgePlacement = isCategoryBlockEnd ? "after" : "before";
          const activeIndex = activeEntry
            ? layout.findIndex(item => item.id === activeEntry.id)
            : -1;
          const targetDirection = edgeCategoryId
            ? "out"
            : entry.type === "category"
              ? "in"
              : activeIndex <= index
                ? "down"
                : "up";
          return (
            <div key={entry.id}>
              <SortableEntryRow
                entry={entry}
                index={index}
                position={
                  entry.type === "list" ? positions.get(entry.id) : undefined
                }
                categoryActive={categoryIsActive(children)}
                categoryLists={children}
                categoryLabel={
                  entry.type === "list" && entry.categoryId
                    ? categoryLabels.get(entry.categoryId)
                    : undefined
                }
                collapsed={
                  entry.type === "category" &&
                  collapsedCategoryIds.has(entry.id)
                }
                isGroupDropTarget={
                  entry.type === "category" &&
                  dropPresentation.groupCategoryId === entry.id
                }
                isBlockDragging={Boolean(isActiveCategoryBlock)}
                onToggleActive={
                  entry.type === "category" && children.length === 0
                    ? undefined
                    : () => toggle(entry)
                }
                onToggleCollapsed={
                  entry.type === "category"
                    ? () => toggleCollapsed(entry.id)
                    : undefined
                }
                onDelete={
                  entry.type === "list" && !entry.protectedItem && onDeleteList
                    ? () => onDeleteList(entry)
                    : entry.type === "category" && onDeleteCategory
                      ? () => onDeleteCategory(entry)
                      : undefined
                }
                onOpen={
                  entry.type === "list" && onOpenList
                    ? () => onOpenList(entry)
                    : undefined
                }
              />
              <SortableDropSlot
                id={
                  edgeCategoryId
                    ? categoryEdgeId(edgeCategoryId, edgePlacement)
                    : `${listDropSlotPrefix}${entry.id}`
                }
                activeDirection={
                  activeSlot?.afterEntryId === entry.id
                    ? activeSlot.direction
                    : undefined
                }
                targetDirection={targetDirection}
                enabled={activeEntry?.type === "list"}
                accept={["list"]}
                data={{
                  categoryId: edgeCategoryId,
                  placement: edgePlacement,
                }}
              />
            </div>
          );
        })}
      </div>
      <DragOverlay dropAnimation={null}>
        {activeEntry ? (
          <div className="w-[min(702px,calc(100vw-32px))]">
            <OverlayEntry
              entry={activeEntry}
              position={
                activeEntry.type === "list"
                  ? positions.get(activeEntry.id)
                  : undefined
              }
              layout={layout}
              categoryLabels={categoryLabels}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DragDropProvider>
  );
}
